<template>
  <div>
    <div v-if="mitreData.length" class="mt-5">
      <div class="d-flex align-center mb-4">
        <div class="headline6">{{ $t("ticketDetails.fullDetails.edrAffectedDevices.mitre") }}</div>
        <v-chip class="ml-2 chip--counter">
          {{ mitreData.length }}
        </v-chip>
      </div>
      <v-table>
        <template #default>
          <tbody>
            <template v-for="(item, index) in mitreData" :key="`row-${index}`">
              <tr :id="`${index}`">
                <td class="pt-2 pb-2">
                  <div class="subtitle1">{{ item.title }}</div>
                  <div class="body2">{{ item.description }}</div>
                  <a class="coro-link" target="_blank" :href="item.link">
                    {{ item.techniqueId }}
                  </a>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-table>
    </div>
    <div v-if="edrAffectedDevices.length" class="mt-11">
      <div class="d-flex align-center mb-4">
        <div class="headline6">{{ $t("ticketDetails.findings") }}</div>
        <v-chip class="ml-2 chip--counter">
          {{ edrAffectedDevicesTotal }}
        </v-chip>
      </div>
      <table-wrapper>
        <v-data-table-server
          :headers="tableHeaders"
          :items="edrAffectedDevices"
          :single-expand="false"
          v-model:expanded="expanded"
          :loading="edrAffectedDevicesLoading"
          disable-pagination
          hide-default-footer
          :items-length="edrAffectedDevicesTotal"
          item-value="id"
          show-expand
          fixed-header
          expand-icon="icon-chevron-down-xs"
        >
          <template #headers>
            <tr>
              <th v-if="showExpander" class="w-10">
                <v-fab size="24" icon="expand-all-icon" color="primary" class="expand-all-icon">
                  <v-icon
                    :class="{ rotated: expandAll }"
                    @click="handleExpandAllToggle"
                    icon="$chevronDown"
                  ></v-icon>
                </v-fab>
              </th>
              <th class="w-80">
                {{ $t("ticketDetails.fullDetails.edrAffectedDevices.device") }}
              </th>
              <th>
                {{ $t("ticketDetails.fullDetails.edrAffectedDevices.executionTime") }}
              </th>
              <th v-if="showProcessGraphColumn">
                {{ $t("ticketDetails.fullDetails.edrAffectedDevices.processGraph") }}
              </th>
            </tr>
          </template>
          <template #item="{ item, index, isExpanded, toggleExpand, internalItem }">
            <tr :id="`${index}`">
              <td v-if="showExpander" class="w-10">
                <v-icon
                  class="ml-1"
                  :class="{ rotated: isExpanded(internalItem) }"
                  @click="toggleExpand(internalItem)"
                  icon="$chevronDown"
                >
                </v-icon>
              </td>
              <td class="w-80 pb-2 pt-2">
                <div class="d-flex justify-center flex-column">
                  <ellipsified-copy-text
                    max-width="200px"
                    class="subtitle1"
                    show-tooltip
                    hide-copy-btn
                    :text="item.hostname"
                  />
                  <ellipsified-copy-text
                    max-width="200px"
                    class="body2"
                    show-tooltip
                    hide-copy-btn
                    :text="item.processName"
                  />
                  <ellipsified-copy-text
                    max-width="200px"
                    class="body2 text-indigo-medium"
                    show-tooltip
                    hide-copy-btn
                    :text="item.userName"
                  />
                </div>
              </td>
              <td>
                <span class="body2 text-indigo-medium">{{
                  getFormattedDateTime(item.executionTime, "MMM, DD YYYY, h:mm A")
                }}</span>
              </td>
              <td v-if="showProcessGraphColumn">
                <div
                  v-if="item.hasProcessGraphData"
                  class="coro-link"
                  @click="handleProcessGraphClick(item)"
                >
                  {{ `${$t("general.view")} ›` }}
                </div>
              </td>
            </tr>
          </template>
          <template #expanded-row="{ columns, item }">
            <tr>
              <td class="w-10"></td>
              <td :colspan="columns.length" class="pl-4 word-break-all white-space-normal">
                <div class="expandable-content">
                  <div class="mr-4 mt-6 mb-6 instance-details">
                    <div class="subtitle1">
                      {{ $t("ticketDetails.fullDetails.edrAffectedDevices.commandLine") }}
                    </div>
                    <ellipsified-copy-text
                      max-width="500px"
                      class="body2 text-indigo-medium"
                      :text="item.commandLine"
                    />
                    <div class="subtitle1 mt-4">
                      {{ $t("ticketDetails.fullDetails.edrAffectedDevices.path") }}
                    </div>
                    <ellipsified-copy-text
                      max-width="500px"
                      class="body2 text-indigo-medium"
                      :text="item.filePath"
                    />
                    <template v-if="showAdditionalUserFields">
                      <template v-if="item.subjectUserName">
                        <div class="subtitle1 mt-4">
                          {{ $t("ticketDetails.fullDetails.edrAffectedDevices.subjectUserName") }}
                        </div>
                        <ellipsified-copy-text
                          max-width="500px"
                          class="body2 text-indigo-medium"
                          :text="item.subjectUserName"
                        />
                      </template>
                      <template v-if="item.targetUserName">
                        <div class="subtitle1 mt-4">
                          {{ $t("ticketDetails.fullDetails.edrAffectedDevices.targetUserName") }}
                        </div>
                        <ellipsified-copy-text
                          max-width="500px"
                          class="body2 text-indigo-medium"
                          :text="item.targetUserName"
                        />
                      </template>
                      <template v-if="item.memberName">
                        <div class="subtitle1 mt-4">
                          {{ $t("ticketDetails.fullDetails.edrAffectedDevices.memberName") }}
                        </div>
                        <ellipsified-copy-text
                          max-width="500px"
                          class="body2 text-indigo-medium"
                          :text="item.memberName"
                        />
                      </template>
                    </template>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </table-wrapper>
      <div v-if="findingsToLoad > 0" class="load-more-section">
        <div class="coro-link cursor-pointer" @click="handleLoadMore">
          {{ $t("ticketDetails.plusNFindings", { n: findingsToLoad }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { getFormattedDateTime } from "@/_helpers/date";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { type EdrDetectionAffectedDevice } from "@/_store/tickets/ticket.module.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import type { EdrMitreTechniqueData } from "@/_store/tickets/tickets.module.ts";
import { TicketTrigger } from "@/constants/tickets.ts";
import { useI18n } from "vue-i18n";

export default defineComponent({
  components: {
    EllipsifiedCopyText,
    TableWrapper,
  },
  props: {
    mitreData: {
      type: Array as PropType<EdrMitreTechniqueData[]>,
      required: true,
      default: () => [],
    },
    edrAffectedDevices: {
      type: Array as PropType<EdrDetectionAffectedDevice[]>,
      required: true,
      default: () => [],
    },
    edrAffectedDevicesLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    edrAffectedDevicesTotal: {
      type: Number,
      required: true,
      default: 0,
    },
    showExpander: {
      type: Boolean,
      required: true,
      default: false,
    },
    findingsToLoad: {
      type: Number,
      required: true,
      default: 0,
    },
    showProcessGraphColumn: {
      type: Boolean,
      required: false,
      default: false,
    },
    eventTrigger: {
      type: String as PropType<TicketTrigger>,
      required: true,
    },
  },
  emits: ["load-more", "process-graph-click"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const expanded = ref<string[]>([]);
    const expandAll = ref(false);

    const showAdditionalUserFields = computed(() => {
      return [
        TicketTrigger.EDR_DETECTION_PRIVILEGE_ESCALATION,
        TicketTrigger.EDR_DETECTION_CREDENTIAL_ACCESS,
        TicketTrigger.EDR_DETECTION_PERSISTENCE,
      ].includes(props.eventTrigger);
    });

    const tableHeaders = computed(() => {
      const headers = [
        {
          title: "",
          key: "data-table-expand",
          sortable: false,
          headerProps: {
            "aria-label": i18n.t("ariaLabels.expanderColumn"),
          },
        },
        {
          key: "name",
          sortable: false,
        },
        { key: "execution-time", sortable: false },
      ];

      if (props.showProcessGraphColumn) {
        headers.push({ key: "process-graph", sortable: false });
      }

      return headers;
    });

    const handleExpandAllToggle = () => {
      expandAll.value = !expandAll.value;
    };

    const handleLoadMore = () => {
      emit("load-more");
    };

    const handleProcessGraphClick = (item: EdrDetectionAffectedDevice) => {
      emit("process-graph-click", item);
    };

    watch(expandAll, (newVal) => {
      if (newVal) {
        expanded.value = [
          ...props.edrAffectedDevices.map((v: EdrDetectionAffectedDevice) => v.enrollmentCode),
        ];
      } else {
        expanded.value = [];
      }
    });

    return {
      tableHeaders,
      expanded,
      expandAll,
      showAdditionalUserFields,
      getFormattedDateTime,
      handleExpandAllToggle,
      handleLoadMore,
      handleProcessGraphClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.expand-all-icon {
  .v-icon::before {
    color: rgb(var(--v-theme-white)) !important;
  }
}

.load-more-section {
  border-left: 1px solid rgb(var(--v-theme-indigo-pale));
  border-right: 1px solid rgb(var(--v-theme-indigo-pale));
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(*) {
  .v-data-table__expanded__content {
    box-shadow: unset !important;
  }

  .v-btn--fab.v-size--small {
    height: 30px;
    width: 30px;
  }
  .v-btn {
    padding: 0 !important;
  }

  .v-table__wrapper {
    box-shadow: unset !important;
    border-radius: unset !important;
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  .full-details-wrapper {
    border: none !important;
  }

  .findings-wrapper {
    padding: 0 !important;
  }
}
</style>
